import {
  Disclaimer,
  Div,
  DropdownMenuComponent,
  getBrandConnectionStatus,
  getLocationConnectionStatus,
  P,
  Radio,
  useOnClickOutside,
  lightTheme,
} from '@vgrubs/components';
import { useEffect, useRef, useState } from 'react';

import { ProviderAndConnectionStatusResponse } from 'components/src/types/api/location.types';
import { Provider } from 'components/src/types/shared.types';
import { useParams } from 'react-router';
import { renderProviderIcon, renderProviderName } from '../../helpers';
import {
  AdminSelectWrapperChildStyled,
  AdminSelectWrapperStyled,
  ChevronStyled,
  ProviderImage,
  SelectedStyled,
  SelectOption,
} from './AdminCustomProviderSelect.styled';

const unified = [
  Provider.gloriafood,
  Provider.doordashPos,
  Provider.ubereats,
  Provider.grubhubPos,
];

export const AdminCustomProviderSelect = <_, T = null>({
  onChange,
  value,
  label,
  providerArray = Provider,
  menu = false,
  additionalOptions = [],
  emptyPlaceholder = 'No data',
  $textCenter = false,
  placeholder = 'Select Provider',
  $shadowDesign = true,
  disabledProviders = [],
  multiSelect = false,
}: {
  onChange: (provider: Provider | T | null | (Provider | T)[]) => void;
  value: Provider | T | null | (Provider | T)[];
  label?: string;
  providerArray?: Record<string | number, Provider> | Provider[];
  menu?: boolean;
  additionalOptions?: T[];
  emptyPlaceholder?: string;
  $textCenter?: boolean;
  placeholder?: string;
  $shadowDesign?: boolean;
  disabledProviders?: (Provider | T)[];
  multiSelect?: boolean;
}) => {
  const [dropdown, setDropdown] = useState(false);
  const [connectionStatuses, setConnectionStatuses] =
    useState<ProviderAndConnectionStatusResponse>([]);

  const { brandId, locationId } = useParams() as {
    brandId: string;
    locationId: string;
  };

  // Ensure disabledProviders is never null to prevent runtime errors
  const safeDisabledProviders = disabledProviders || [];

  // Helper to get current values as array
  const getCurrentValues = (): (Provider | T)[] => {
    if (multiSelect) {
      return Array.isArray(value) ? value : value ? [value] : [];
    }
    return value ? [value as Provider | T] : [];
  };

  // Helper to check if item is selected
  const isSelected = (item: Provider | T): boolean => {
    const currentValues = getCurrentValues();
    return currentValues.includes(item);
  };

  // Delivery.com is no longer offered. It stays an option only where it is
  // already selected, so existing rules and prices still read correctly.
  const isOffered = (p: Provider) =>
    p !== Provider.delivery || isSelected(Provider.delivery);

  const options = Object.values(providerArray).filter(isOffered);

  const optionsForMenu = Object.values(Provider)
    .filter((p) => !unified.includes(p))
    .filter((p) => p !== Provider.nabe)
    .filter(isOffered);

  async function getProvidersStatuses() {
    try {
      const isLocations = brandId === '0';
      const getAction = isLocations
        ? getLocationConnectionStatus
        : getBrandConnectionStatus;
      const id = isLocations ? locationId : brandId;
      const { data } = await getAction(id);
      setConnectionStatuses(data.data);
    } catch (error) {
      console.error(error);
    }
  }

  const dropdownRef = useRef(null);
  useOnClickOutside(dropdownRef, () => dropdown && setDropdown(false));

  const handleSelect = (selectedValue: Provider | T | null) => {
    if (multiSelect) {
      const currentValues = getCurrentValues();
      let newValues: (Provider | T)[];

      if (selectedValue === null) {
        newValues = [];
      } else if (currentValues.includes(selectedValue)) {
        // Remove if already selected
        newValues = currentValues.filter((v) => v !== selectedValue);
      } else {
        // Add if not selected
        newValues = [...currentValues, selectedValue];
      }

      onChange(newValues);
    } else {
      onChange(selectedValue);
      setDropdown(false);
    }
  };

  const UNIFIED = Provider.gloriafood;

  useEffect(() => {
    if (!multiSelect) {
      setDropdown(false);
    }
    if (value === UNIFIED && menu) getProvidersStatuses();
  }, [value, multiSelect]);

  // Render selected items for multi-select
  const RenderSelectedProviders = () => {
    const currentValues = getCurrentValues();

    if (currentValues.length === 0) {
      return placeholder;
    }

    if (multiSelect) {
      return (
        <>
          <Div $desktop $flex $wrap $gap="12px" $gapSM="8px" $alignCenter>
            {currentValues.slice(0, 2).map((item, index) => (
              <Div key={index} $flex $alignCenter $gap="8px" $gapSM="4px">
                <ProviderImage image={renderProviderIcon(item as Provider)}>
                  <img src={renderProviderIcon(item as Provider)} />
                </ProviderImage>
                <>
                  {renderProviderName(item as string, item as string)}
                  {index !== currentValues.length - 1 && ','}
                </>
              </Div>
            ))}
            {currentValues.length > 2 && (
              <Disclaimer $m="0" $color={lightTheme.darkGray}>
                +{currentValues.length - 2} more
              </Disclaimer>
            )}
          </Div>
          <Div $mobile $flex $wrap $gap="12px" $gapSM="8px" $alignCenter>
            {currentValues.slice(0, 1).map((item, index) => (
              <Div key={index} $flex $alignCenter $gap="8px" $gapSM="4px">
                <ProviderImage image={renderProviderIcon(item as Provider)}>
                  <img src={renderProviderIcon(item as Provider)} />
                </ProviderImage>
                <>
                  {renderProviderName(item as string, item as string)}
                  {index !== currentValues.length - 1 && ','}
                </>
              </Div>
            ))}
            {currentValues.length > 1 && (
              <Disclaimer $m="0" $color={lightTheme.darkGray}>
                +{currentValues.length - 1} more
              </Disclaimer>
            )}
          </Div>
        </>
      );
    } else {
      // Single select display logic
      const singleValue = currentValues[0];
      return (
        <Div $flex $alignCenter $gap="8px" $gapSM="4px">
          <ProviderImage image={renderProviderIcon(singleValue as Provider)}>
            <img src={renderProviderIcon(singleValue as Provider)} />
          </ProviderImage>
          {renderProviderName(singleValue as string, singleValue as string)}
        </Div>
      );
    }
  };

  return (
    <div>
      {label && (
        <P $mb="8" $ml="24" $mr="24" $center={$textCenter}>
          <b>{label}</b>
        </P>
      )}

      <AdminSelectWrapperStyled
        $opened={dropdown}
        onClick={() => setDropdown(true)}
        ref={dropdownRef}
        $shadowDesign={$shadowDesign}
      >
        <AdminSelectWrapperChildStyled>
          <Div $flex $center={$textCenter} $width="100%">
            <SelectedStyled>
              {menu && value && value === UNIFIED ? (
                <Div $flex $gap="4px 16px">
                  {unified.map((p, i) => {
                    const connected =
                      connectionStatuses.find((s) => s.provider === p)
                        ?.isConnected ?? false;
                    return (
                      <Div
                        $flex
                        $alignCenter
                        $flexNone
                        key={i}
                        $gap="12px"
                        style={{ opacity: connected ? 1 : 0.4 }}
                      >
                        <ProviderImage image={renderProviderIcon(p)}>
                          <img src={renderProviderIcon(p)} />
                        </ProviderImage>
                        {renderProviderName(p)}
                      </Div>
                    );
                  })}
                </Div>
              ) : (
                <RenderSelectedProviders />
              )}
            </SelectedStyled>
          </Div>

          <ChevronStyled>
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M7.29365 12.7066C7.68428 13.0973 8.31865 13.0973 8.70928 12.7066L14.7093 6.70664C15.0999 6.31602 15.0999 5.68164 14.7093 5.29102C14.3187 4.90039 13.6843 4.90039 13.2937 5.29102L7.9999 10.5848L2.70615 5.29414C2.31553 4.90352 1.68115 4.90352 1.29053 5.29414C0.899902 5.68477 0.899902 6.31914 1.29053 6.70977L7.29053 12.7098L7.29365 12.7066Z"
                fill="#4C4C4C"
              />
            </svg>
          </ChevronStyled>
        </AdminSelectWrapperChildStyled>

        <DropdownMenuComponent isOpen={dropdown} maxHeight={'270px'} fullWidth>
          {menu ? (
            <Div $flex $column $gap="10px">
              <SelectOption
                selected={UNIFIED === value}
                onClick={() => handleSelect(UNIFIED)}
              >
                {multiSelect && <Radio checked={isSelected(UNIFIED)} />}
                <Div $flex $wrap $gap="4px 16px">
                  {unified.map((p, i) => (
                    <Div $flex $alignCenter $flexNone key={i} $gap="12px">
                      <ProviderImage image={renderProviderIcon(p)}>
                        <img src={renderProviderIcon(p)} />
                      </ProviderImage>
                      {renderProviderName(p)}
                    </Div>
                  ))}
                </Div>
              </SelectOption>
              {optionsForMenu.map((v, i) => (
                <SelectOption
                  selected={isSelected(v)}
                  onClick={() => handleSelect(v)}
                  key={i}
                >
                  {multiSelect && <Radio checked={isSelected(v)} />}
                  <Div $flex $alignCenter $flexNone key={i} $gap="12px">
                    <ProviderImage image={renderProviderIcon(v)}>
                      <img src={renderProviderIcon(v)} />
                    </ProviderImage>
                    {renderProviderName(v)}
                  </Div>
                </SelectOption>
              ))}
            </Div>
          ) : (
            <Div $flex $column $gap="8px">
              {options
                .filter((v) => !safeDisabledProviders.includes(v))
                .map((v, i) => (
                  <SelectOption
                    selected={isSelected(v)}
                    onClick={() => handleSelect(v)}
                    key={i}
                  >
                    <Div $flex $alignCenter $gap="12px">
                      <ProviderImage image={renderProviderIcon(v)}>
                        <img src={renderProviderIcon(v)} />
                      </ProviderImage>
                      {renderProviderName(v)}
                    </Div>
                    {multiSelect && <Radio checked={isSelected(v)} />}
                  </SelectOption>
                ))}
              {additionalOptions.map((v, i) => (
                <SelectOption
                  selected={isSelected(v)}
                  onClick={() => handleSelect(v)}
                  key={i}
                >
                  <Div $height="28px" $ml="20" $flex $center>
                    {v as string}
                  </Div>
                  {multiSelect && <Radio checked={isSelected(v)} />}
                </SelectOption>
              ))}
            </Div>
          )}
          {options.filter((v) => !safeDisabledProviders.includes(v)).length <
            1 && <Disclaimer $m="0">{emptyPlaceholder}</Disclaimer>}
        </DropdownMenuComponent>
      </AdminSelectWrapperStyled>
    </div>
  );
};
